import { SelectedDeliveryOption } from "../types";
import { CPTSlabForManualOption, TransactionType } from "../../../views/Orders/enums";
import { formatMinInstantDeliveryChargeMessage } from "../../../constants/logisticsCptSlabs";
import { TRADING_SELF_PICKUP_BLOCKED_MESSAGE } from "../utils/estimateTradingSelfPickupValidation";
import { DeliverySupplierOption } from "../types";
import {
  getManualFulfillmentSupplierRestrictionMessage,
  isManualFulfillmentSupplierValidForCptSlab,
} from "../utils/resolveManualFulfillmentSupplierForCptSlab";

export interface ValidationConfig {
  instantDeliveryMinimumDeliveryCost?: number;
  transactionType?: string;
  allowTradingSelfPickup?: boolean;
  isManualFulfillmentRoutingRestricted?: boolean;
  supplierOptions?: DeliverySupplierOption[];
}

export interface ValidationError {
  id: string;
  message: string;
  targetField?: "deliveryCharge" | "cpt" | "cptSlab" | "general";
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  errorsByField: Record<string, ValidationError[]>;
}

export interface DeliverySelectionValidationRule {
  id: string;
  targetField?: "deliveryCharge" | "cpt" | "cptSlab" | "general";

  shouldApply: (
    selection: SelectedDeliveryOption,
    config: ValidationConfig,
  ) => boolean;

  validate: (
    selection: SelectedDeliveryOption,
    config: ValidationConfig,
  ) => boolean;

  getErrorMessage: (
    selection: SelectedDeliveryOption,
    config: ValidationConfig,
  ) => string;
}

export const DELIVERY_SELECTION_VALIDATION_RULES: DeliverySelectionValidationRule[] =
  [
    {
      id: "manual-instant-delivery-min-delivery-charge",
      targetField: "deliveryCharge",
      shouldApply: (selection) =>
        selection.deliveryDetails?.selectionType === "manual" &&
        selection.deliveryDetails?.manualEntry?.cptSlab ===
          CPTSlabForManualOption.INSTANT_DELIVERY,
      validate: (selection, config) => {
        const charge = selection.deliveryDetails?.manualEntry?.deliveryCharge;
        const minCharge = config.instantDeliveryMinimumDeliveryCost ?? 50;
        return charge !== undefined && charge !== null && charge >= minCharge;
      },
      getErrorMessage: (selection, config) => {
        const minCharge = config.instantDeliveryMinimumDeliveryCost ?? 50;
        return formatMinInstantDeliveryChargeMessage(minCharge);
      },
    },
    {
      id: "manual-trading-self-pickup-not-allowed",
      targetField: "cptSlab",
      shouldApply: (selection, config) =>
        selection.deliveryDetails?.selectionType === "manual" &&
        selection.deliveryDetails?.manualEntry?.cptSlab ===
          CPTSlabForManualOption.SELF_PICKUP &&
        config.transactionType === TransactionType.TRADING &&
        !config.allowTradingSelfPickup,
      validate: () => false,
      getErrorMessage: () => TRADING_SELF_PICKUP_BLOCKED_MESSAGE,
    },
    {
      id: "manual-fulfillment-supplier-routing-restricted",
      targetField: "general",
      shouldApply: (selection, config) =>
        !!config.isManualFulfillmentRoutingRestricted &&
        selection.deliveryDetails?.selectionType === "manual" &&
        selection.deliveryDetails?.manualEntry?.cptSlab != null,
      validate: (selection, config) => {
        const entry = selection.deliveryDetails?.manualEntry;
        return isManualFulfillmentSupplierValidForCptSlab({
          cptSlab: entry?.cptSlab,
          supplierId: entry?.supplierId || selection.supplierId,
          supplierOptions: config.supplierOptions,
          isRestricted: true,
        });
      },
      getErrorMessage: (selection) =>
        getManualFulfillmentSupplierRestrictionMessage(
          selection.deliveryDetails?.manualEntry?.cptSlab,
        ),
    },
  ];
